import React from "react";

import classnames from "classnames";
import {
  CircleCheckIcon,
  ClockIcon,
  Loader2Icon,
  OctagonXIcon,
  RotateCcwIcon,
  XIcon,
} from "lucide-react";
import { useTranslation } from "react-i18next";
import { useHistory } from "react-router-dom";

import type {
  UploadJob,
  UploadJobStatus,
} from "../../contexts/uploadQueue.types";

interface UploadToastProps {
  job: UploadJob;
  onCancel: (jobId: string) => void;
  onRemove: (jobId: string) => void;
  onRetry: (jobId: string) => void;
  // Number of other uploads queued behind this one. When > 0 on the active
  // upload, a "+N en attente" badge is shown (hybrid single-toast mode).
  queuedCount?: number;
}

function StatusIcon({
  status,
}: {
  status: UploadJobStatus;
}): React.JSX.Element {
  switch (status) {
    case "uploading":
      return (
        <Loader2Icon className="text-primary-600 size-5 shrink-0 animate-spin" />
      );
    case "completed":
      return <CircleCheckIcon className="size-5 shrink-0 text-emerald-500" />;
    case "error":
      return <OctagonXIcon className="size-5 shrink-0 text-red-500" />;
    default:
      return <ClockIcon className="text-muted-foreground size-5 shrink-0" />;
  }
}

const ICON_BUTTON_CLASSNAMES =
  "shrink-0 rounded-md p-1 text-muted-foreground hover:bg-muted hover:text-foreground";

export default function UploadToast({
  job,
  onCancel,
  onRemove,
  onRetry,
  queuedCount = 0,
}: UploadToastProps): React.JSX.Element {
  const { t } = useTranslation("editor-content");
  const history = useHistory();

  function withStop(handler: () => void) {
    return (event: React.MouseEvent): void => {
      event.stopPropagation();
      handler();
    };
  }

  // Self-contained card chrome. Sonner sets data-styled=false on custom toasts
  // (they carry `jsx`), so they never inherit the Toaster's popover background
  // or border-radius — we must provide the surface ourselves.
  const cardClassNames = classnames(
    "flex w-80 cursor-pointer flex-col gap-2.5 rounded-md border bg-card px-4 py-3 shadow-lg ring-1 ring-border",
    job.status === "error" ? "border-destructive/40" : "border-border",
  );

  const statusLabelClassNames = classnames("text-xs", {
    "text-emerald-600": job.status === "completed",
    "text-red-500": job.status === "error",
    "text-primary-600": job.status === "uploading",
    "text-muted-foreground": job.status === "pending",
  });

  const progressBarClassNames = classnames(
    "h-full rounded-full transition-all duration-500",
    job.progress === 100 ? "bg-emerald-500" : "bg-primary",
  );

  const HIDE_BUTTON_UNTIL_BACKEND_FIX = true;

  return (
    <div
      className={cardClassNames}
      onClick={(): void => history.push(job.navigatePath)}
      role="button"
      tabIndex={0}
    >
      <div className="flex items-center gap-3">
        <StatusIcon status={job.status} />

        <div className="min-w-0 flex-1">
          <p className="text-foreground mb-1 truncate text-xs font-medium">
            {job.displayName}
          </p>
          <div className="flex items-center gap-1.5 whitespace-nowrap">
            <span className={statusLabelClassNames}>
              {t(`uploadToast.${job.status}`)}
            </span>
            {job.status === "uploading" && queuedCount > 0 ? (
              <span className="text-2xs bg-muted text-muted-foreground rounded-full px-1.5 py-0.5 font-medium">
                +{queuedCount} {t("uploadToast.queued")}
              </span>
            ) : null}
          </div>
        </div>

        {job.status === "uploading" && (
          <span className="text-primary-600 shrink-0 text-xs font-medium tabular-nums">
            {job.progress}%
          </span>
        )}

        {job.status === "uploading" && !HIDE_BUTTON_UNTIL_BACKEND_FIX && (
          <button
            aria-label={t("uploadToast.cancel")}
            className={ICON_BUTTON_CLASSNAMES}
            onClick={withStop((): void => onCancel(job.id))}
            type="button"
          >
            <XIcon className="size-4" />
          </button>
        )}

        {job.status === "pending" && (
          <button
            aria-label={t("uploadToast.remove")}
            className={ICON_BUTTON_CLASSNAMES}
            onClick={withStop((): void => onRemove(job.id))}
            type="button"
          >
            <XIcon className="size-4" />
          </button>
        )}

        {job.status === "error" && (
          <div className="flex shrink-0 items-center gap-1">
            <button
              aria-label={t("uploadToast.retry")}
              className="hover:text-primary-600 text-muted-foreground hover:bg-muted rounded-md p-1"
              onClick={withStop((): void => onRetry(job.id))}
              type="button"
            >
              <RotateCcwIcon className="size-4" />
            </button>
            <button
              aria-label={t("uploadToast.remove")}
              className="text-muted-foreground hover:bg-destructive/10 hover:text-destructive rounded-md p-1"
              onClick={withStop((): void => onRemove(job.id))}
              type="button"
            >
              <XIcon className="size-4" />
            </button>
          </div>
        )}
      </div>

      {job.status === "uploading" && (
        <div className="bg-surface-muted h-1.5 w-full overflow-hidden rounded-full">
          <div
            className={progressBarClassNames}
            style={{ width: `${job.progress}%` }}
          />
        </div>
      )}
    </div>
  );
}
